import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useParams } from 'react-router-dom';
import { loginMotorista, isMotoristaLoggedIn } from '../../services/motoristaAuth';
import { Config } from '../../services/config';
import '../../components/Auth/Login.css';

interface EmpresaInfo {
  id: number;
  nome_fantasia: string;
  razao_social: string;
  logomarca_url?: string;
  pwa_slug: string;
}

const MotoristaLogin: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();
  const [cpf, setCpf] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [empresa, setEmpresa] = useState<EmpresaInfo | null>(null);
  const [loadingEmpresa, setLoadingEmpresa] = useState(!!slug);

  useEffect(() => {
    if (slug) {
      fetchEmpresa(slug);
    }
  }, [slug]);

  const fetchEmpresa = async (slugParam: string) => {
    try {
      const response = await fetch(`${Config.API_BASE_URL}/public/empresa/${slugParam}`);
      const data = await response.json();
      if (data.success) {
        setEmpresa(data.data);
      } else {
        setError('Empresa nao encontrada. Verifique o link de acesso.');
      }
    } catch {
      setError('Erro ao carregar dados da empresa.');
    } finally {
      setLoadingEmpresa(false);
    }
  };

  if (isMotoristaLoggedIn()) {
    return <Navigate to="/motorista" replace />;
  }

  const formatCpf = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await loginMotorista(cpf, pin, slug);
      if (result.success) {
        navigate('/motorista', { replace: true });
      } else {
        setError(result.message || 'Erro no login');
      }
    } catch {
      setError('Erro de conexao');
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingEmpresa) {
    return (
      <div className="login-container" style={{ background: 'linear-gradient(135deg, #04C64F 0%, #028a37 100%)' }}>
        <div className="login-card" style={{ maxWidth: '400px', textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '32px', color: '#04C64F' }}></i>
          <p style={{ marginTop: '15px', color: '#7f8c8d' }}>Carregando...</p>
        </div>
      </div>
    );
  }

  const nomeEmpresa = empresa?.nome_fantasia || empresa?.razao_social;

  return (
    <div className="login-container" style={{ background: 'linear-gradient(135deg, #04C64F 0%, #028a37 100%)' }}>
      <div className="login-card" style={{ maxWidth: '400px' }}>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          {empresa?.logomarca_url ? (
            <img
              src={empresa.logomarca_url}
              alt={nomeEmpresa || 'Logo'}
              style={{ maxHeight: '60px', maxWidth: '200px', marginBottom: '10px', objectFit: 'contain' }}
            />
          ) : (
            <i className="fas fa-truck" style={{ fontSize: '48px', color: '#04C64F', marginBottom: '10px' }}></i>
          )}
          <h1 style={{ fontSize: '1.5rem', color: '#2c3e50', margin: 0 }}>
            {nomeEmpresa || 'ConfereAi - Motorista'}
          </h1>
          <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>Acesse com seu CPF e PIN</p>
        </div>

        {error && (
          <div style={{ background: '#fce4ec', color: '#c62828', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '0.9rem' }}>
            <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>{error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#2c3e50' }}>CPF</label>
            <input
              type="text"
              value={cpf}
              onChange={(e) => setCpf(formatCpf(e.target.value))}
              placeholder="000.000.000-00"
              className="form-control"
              style={{ fontSize: '1.1rem', padding: '12px', textAlign: 'center', letterSpacing: '2px' }}
              inputMode="numeric"
              autoComplete="off"
              required
            />
          </div>

          <div style={{ marginBottom: '25px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', color: '#2c3e50' }}>PIN</label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="Digite seu PIN"
              className="form-control"
              style={{ fontSize: '1.3rem', padding: '12px', textAlign: 'center', letterSpacing: '8px' }}
              inputMode="numeric"
              autoComplete="off"
              required
              minLength={4}
              maxLength={8}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || cpf.replace(/\D/g, '').length !== 11 || pin.length < 4}
            style={{
              width: '100%', padding: '14px', fontSize: '1rem', fontWeight: '600',
              background: isLoading ? '#ccc' : '#04C64F', color: 'white', border: 'none',
              borderRadius: '8px', cursor: isLoading ? 'not-allowed' : 'pointer'
            }}
          >
            {isLoading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '0.8rem', color: '#95a5a6' }}>
          Nao tem PIN? Solicite ao gestor da frota.
        </p>
      </div>
    </div>
  );
};

export default MotoristaLogin;
